<template>
    <div :class="['companyindex-search', { 'companyindex-search--focus': hasFocus }]" role="combobox" :aria-expanded="hasFocus && completions.length || searching ? 'true' : 'false'">
        <input
            v-bind="$attrs"
            :class="['companyindex-search__input', 'form-control', { 'form-control-lg': size === 'large' }]"
            ref="input"
            role="searchbox"
            type="text"
            v-model="query"
            @focus="hasFocus = true"
            @blur="handleBlur"
            @keydown.enter.prevent="goToProfile($event, completionSelectIndex)"
            @keydown.down.prevent="selectNext"
            @keydown.up.prevent="selectPrev"
            :placeholder="T.fieldPlaceholder()"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            aria-autocomplete="list"
            aria-controls="companyindex-search__completions"
        >
        <ul
            class="companyindex-search__completions"
            id="companyindex-search__completions"
            role="listbox"
            v-if="(hasFocus && completions.length) || searching"
        >
            <li
                v-if="searching"
                class="companyindex-search__completionitem companyindex-search__completionitem--focus"
            >
                {{ T.searching() }}
            </li>
            <li
                :class="{ 'companyindex-search__completionitem': true, 'companyindex-search__completionitem--focus': completionSelectIndex == index }"
                v-for="(item, index) in completions"
                @click.stop="goToProfile(null, index)"
                :key="item.id"
            >
                <div class="d-block d-md-inline companyindex-search__completionitem__companyname align-true-center">
                    {{ item.text }}
                </div>
                <div class="text-xs text-muted d-md-inline align-true-center">
                    <company-rating-stars
                        v-if="item.rating"
                        :rating="item.rating"
                        size="inline"
                        class="ml-md-1"
                    />
                    <span
                        v-if="item.hits"
                    >
                        <span
                            :class="{ 'mx-1': true, 'd-none': !item.rating, 'd-md-inline': !item.rating }"
                        >
                            &bull;
                        </span>
                        {{ T.jobCount(item.hits) }}
                    </span>
                </div>
                <span
                    v-if="item.logo"
                    v-html="item.logo"
                    class="float-right companyindex-search__completionitem__logo d-none d-md-inline"
                />
            </li>
        </ul>
        <button type="submit" ref="submitter" class="d-none" />
    </div>
</template>

<script>
import T from './CompanyIndexSearchField.i18n.js';

import CompanyRatingStars from 'JIX/components/CompanyRatingStars.vue';

export default {
    inheritAttrs: false,
    components: {
        CompanyRatingStars
    },
    props: {
        value: {
            type: String,
            default: "",
        },
        size: {
            type: String,
            default: 'normal',
            validator: (val) => ['normal', 'large'].includes(val),
        },
        tab: {
            type: String,
            default: "about",
        },
        suggestionUrl: {
            type: String,
            default: "/api/suggestions/company/public",
        },
        completionDelay: {
            type: Number,
            default: 500,
        },
    },
    data() {
        return {
            query: this.value,
            completions: [],
            completionSelectIndex: 0,
            completionDelayTimeout: null,
            completionRequest: null,
            completionAbortController: null,
            completionLastUrl: '',
            hasFocus: false,
            blurTimeout: null,
            searching: false,
            T: T,
        };
    },
    computed: {
    },
    watch: {
        query() {
            this.requestCompletionsUpdate();
        },
    },
    methods: {
        requestCompletionsUpdate(opts = {}) {
            const { batchRequest = true } = opts;

            if (!batchRequest) {
                this.updateCompletions();
            } else {
                const startOfBatch = !this.completionDelayTimeout && batchRequest;
                if (startOfBatch) {
                    this.searching = true;
                    this.completionDelayTimeout = setTimeout(() => {
                        this.completionDelayTimeout = null;
                        this.updateCompletions();
                    }, this.completionDelay);
                }
            }
        },
        updateCompletions() {
            if (this.completionAbortController) {
                this.completionAbortController.abort();
            }
            this.completionAbortController = new AbortController();
            const signal = this.completionAbortController.signal;

            const params = [ ['company', this.query], ['use_ad_search', 1], ['tab', this.tab] ];
            const url = `${this.suggestionUrl}?${new URLSearchParams(params)}`;
            if (url === this.completionLastUrl) {
                return;
            }
            this.completionLastUrl = url;
            this.searching = true;
            const req = this.completionRequest = fetch(url, { signal })
                .then(resp => resp.json())
                .catch(error => {
                    if (!(error instanceof DOMException && error.name === 'AbortError')) {
                        console.error('Error when fetching suggestions', error);
                    }
                    return { result: [] };
                })
                .then(data => {
                    if (this.completionRequest === req) {
                        this.searching = false;
                        this.completions = data.result;
                        this.completionSelectIndex = -1;
                    }
                });
        },
        focusInput() {
            this.$refs.input.focus();
        },
        goToProfile(event, index) {
            if (index < 0) {
                this.$refs.submitter.click();
            }
            const selected = this.completions[index];
            if (selected) {
                if (event) {
                    event.preventDefault();
                }
                this.query = selected.text;
                window.location = selected.url;
            }
        },
        selectNext() {
            this.completionSelectIndex++;
            this.constrainSelectIndex();
        },
        selectPrev() {
            this.completionSelectIndex--;
            this.constrainSelectIndex();
        },
        constrainSelectIndex() {
            if (this.completionSelectIndex >= this.completions.length) {
                this.completionSelectIndex = this.completions.length - 1;
            }
            if (this.completionSelectIndex < -1) {
                this.completionSelectIndex = -1;
            }

            let offset = document.querySelector('#companyindex-search__completions li:nth-child(' + (this.completionSelectIndex + 1) + ')').offsetTop;
            document.getElementById('companyindex-search__completions').scrollTop = offset;
        },
        handleBlur() {
            // Race condition: When clicking on a suggestion, the blur event
            // will trigger first, hiding the suggestion dropdown which means the
            // click event never triggers. Wait a bit before hiding the
            // dropdown to allow the click event to fire.
            this.blurTimeout = setTimeout(() => {
                this.hasFocus = false;
            }, 310);
        },
    },
};
</script>
